/**
 * Collections ladder and the owner money board (owner-research money (f)
 * items 2 and 4). Imported as `@velora/contracts/collections-ladder`.
 * Ladder routes under `/v1/collections/ladder`; the board is
 * `GET /v1/analytics/money-board`.
 *
 * The ladder is a policy-driven cadence per office: a statement, text-to-pay
 * on days 0, 7 and 14, an email, AI courtesy-call tasks on days 30 and 60,
 * a final notice, then an agency-ready export. Autopay is offered at every
 * touch. Fair-debt-style limits apply to every touch: at most 7 contacts in
 * any 7 days, quiet hours (8 AM - 8 PM office time, and a 9 PM hard stop for
 * calls), and stop on request.
 *
 * OFF by default per office, and every touch also asks the master gate
 * (Settings > Automations & notifications). A touch the gate refuses is
 * recorded as `held` with the reason.
 */
import { z } from 'zod';

const Uuid = z.uuid();
const IsoDate = z.iso.date();
const Instant = z.iso.datetime({ offset: true });

export const LADDER_CHANNELS = [
  'statement',
  'text',
  'email',
  'call',
  'final_notice',
  'agency_export',
] as const;
export type LadderChannel = (typeof LADDER_CHANNELS)[number];
export const LadderChannelSchema = z.enum(LADDER_CHANNELS);

export const LadderStepSchema = z.object({
  /** Stable key, e.g. `text-7`. */
  key: z.string().regex(/^[a-z0-9-]{2,40}$/),
  /** Days since the ladder started (day 0 = enrolment). */
  day: z.number().int().min(0).max(365),
  channel: LadderChannelSchema,
});
export type LadderStep = z.infer<typeof LadderStepSchema>;

export const CollectionsPolicySchema = z.object({
  locationId: Uuid,
  enabled: z.boolean(),
  /** Families owing less than this are not enrolled. */
  minBalanceCents: z.number().int().min(0),
  /** Days past the oldest charge before enrolment. */
  enrolAfterDays: z.number().int().min(0).max(180),
  steps: z.array(LadderStepSchema).min(1).max(20),
  /** Most contacts (texts, emails, calls, notices) in any 7 days. */
  maxContactsPer7Days: z.number().int().min(1).max(7),
  /** Offer autopay in every touch. */
  offerAutopay: z.boolean(),
  version: z.number().int().positive(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type CollectionsPolicy = z.infer<typeof CollectionsPolicySchema>;

export const CollectionsPolicyPutSchema = CollectionsPolicySchema.omit({
  locationId: true,
  version: true,
  updatedAt: true,
  updatedByName: true,
})
  .partial()
  .extend({ expectedVersion: z.number().int().positive() })
  .strict();
export type CollectionsPolicyPut = z.infer<typeof CollectionsPolicyPutSchema>;

export const DEFAULT_LADDER_STEPS: readonly LadderStep[] = Object.freeze([
  { key: 'statement-0', day: 0, channel: 'statement' },
  { key: 'text-0', day: 0, channel: 'text' },
  { key: 'text-7', day: 7, channel: 'text' },
  { key: 'text-14', day: 14, channel: 'text' },
  { key: 'email-21', day: 21, channel: 'email' },
  { key: 'call-30', day: 30, channel: 'call' },
  { key: 'call-60', day: 60, channel: 'call' },
  { key: 'final-75', day: 75, channel: 'final_notice' },
  { key: 'agency-90', day: 90, channel: 'agency_export' },
]);

export const DEFAULT_COLLECTIONS_POLICY = Object.freeze({
  enabled: false,
  minBalanceCents: 2_500,
  enrolAfterDays: 30,
  steps: DEFAULT_LADDER_STEPS,
  maxContactsPer7Days: 7,
  offerAutopay: true,
});

export const LADDER_STATUSES = [
  'active',
  'paused',
  /** The patient asked us to stop, or opted out everywhere. */
  'stopped',
  'paid',
  'agency_ready',
] as const;
export type LadderStatus = (typeof LADDER_STATUSES)[number];

export const TOUCH_STATUSES = ['sent', 'queued', 'held', 'skipped', 'failed'] as const;
export type TouchStatus = (typeof TOUCH_STATUSES)[number];

export const LadderTouchSchema = z.object({
  id: Uuid,
  stepKey: z.string(),
  channel: LadderChannelSchema,
  status: z.enum(TOUCH_STATUSES),
  reason: z.string().nullable(),
  autopayOffered: z.boolean(),
  at: Instant,
});
export type LadderTouch = z.infer<typeof LadderTouchSchema>;

export const LadderRowSchema = z.object({
  id: Uuid,
  guarantorId: Uuid,
  guarantorName: z.string(),
  startedOn: IsoDate,
  day: z.number().int().nonnegative(),
  balanceCents: z.number().int(),
  status: z.enum(LADDER_STATUSES),
  stopReason: z.string().nullable(),
  nextStep: LadderStepSchema.nullable(),
  /** Plain English: why the next step is waiting (limit, quiet hours, gate). */
  nextNote: z.string().nullable(),
  touches: z.array(LadderTouchSchema),
  contactsLast7Days: z.number().int().nonnegative(),
});
export type LadderRow = z.infer<typeof LadderRowSchema>;

export const LadderListSchema = z.object({
  locationId: Uuid,
  policy: CollectionsPolicySchema,
  gateNote: z.string().nullable(),
  rows: z.array(LadderRowSchema),
  counts: z.object({
    active: z.number().int().nonnegative(),
    stopped: z.number().int().nonnegative(),
    agencyReady: z.number().int().nonnegative(),
    heldTouches: z.number().int().nonnegative(),
  }),
});
export type LadderList = z.infer<typeof LadderListSchema>;

export const LadderStopSchema = z.object({ reason: z.string().min(2).max(200) }).strict();

/* ------------------------------------------------------------------------ */
/* Owner money board                                                         */
/* ------------------------------------------------------------------------ */

export const MONEY_KPIS = [
  'collection_pct',
  'days_in_ar',
  'patient_ar_over_90_pct',
  'insurance_ar_over_90_pct',
  'first_pass_pct',
  'acceptance_pct',
] as const;
export type MoneyKpi = (typeof MONEY_KPIS)[number];

export interface MoneyBenchmark {
  readonly kpi: MoneyKpi;
  readonly label: string;
  readonly unit: 'percent' | 'days';
  readonly better: 'higher' | 'lower';
  /** At or past this is good (green). */
  readonly good: number;
  /** Past this (the wrong way) is a problem (red); between is fair. */
  readonly poor: number;
  readonly note: string;
  readonly source: string;
}

/** The benchmarks from owner-research-money.md (a). */
export const MONEY_BENCHMARKS: readonly MoneyBenchmark[] = Object.freeze([
  {
    kpi: 'collection_pct',
    label: 'Collection %',
    unit: 'percent',
    better: 'higher',
    good: 98,
    poor: 95,
    note: 'Net collections / net production. Good is 95-98%.',
    source: 'Pulse',
  },
  {
    kpi: 'days_in_ar',
    label: 'Days in AR',
    unit: 'days',
    better: 'lower',
    good: 30,
    poor: 45,
    note: 'Total AR / average daily net production (90 days). Typical 30-45; good under 30.',
    source: 'Viva',
  },
  {
    kpi: 'patient_ar_over_90_pct',
    label: 'Patient AR over 90',
    unit: 'percent',
    better: 'lower',
    good: 10,
    poor: 20,
    note: 'Share of patient AR older than 90 days. Typical 15-20%; good 5-10%.',
    source: 'Viva; Burkhart',
  },
  {
    kpi: 'insurance_ar_over_90_pct',
    label: 'Insurance AR over 90',
    unit: 'percent',
    better: 'lower',
    good: 10,
    poor: 20,
    note: 'Share of outstanding claim dollars sent more than 90 days ago. Keep under 20%.',
    source: 'DentistryIQ',
  },
  {
    kpi: 'first_pass_pct',
    label: 'First-pass %',
    unit: 'percent',
    better: 'higher',
    good: 95,
    poor: 81,
    note: 'Claims paid on the first remittance, not denied. Clean is 95-98%; about 19% are denied first time.',
    source: 'Clerri; DentistryIQ',
  },
  {
    kpi: 'acceptance_pct',
    label: 'Case acceptance',
    unit: 'percent',
    better: 'higher',
    good: 70,
    poor: 42,
    note: 'Accepted / presented treatment dollars. Average 42%; top 10% reach 75%.',
    source: 'HS One Catalyst Index',
  },
]);

export const MoneyRatingSchema = z.enum(['good', 'fair', 'poor', 'unavailable']);
export type MoneyRating = z.infer<typeof MoneyRatingSchema>;

export const MoneyCellSchema = z.object({
  kpi: z.enum(MONEY_KPIS),
  value: z.number().nullable(),
  rating: MoneyRatingSchema,
  /** Why the value is missing (not synced, no remittances yet...). */
  unavailable: z.string().nullable(),
  /** The parts behind the number, in plain words ("$12,400 of $13,100"). */
  detail: z.string().nullable(),
});
export type MoneyCell = z.infer<typeof MoneyCellSchema>;

export const MoneyBoardSchema = z.object({
  asOf: IsoDate,
  windowDays: z.number().int().positive(),
  offices: z.array(
    z.object({
      locationId: Uuid,
      name: z.string(),
      cells: z.array(MoneyCellSchema),
    }),
  ),
  generatedAt: Instant,
});
export type MoneyBoard = z.infer<typeof MoneyBoardSchema>;
